import { useState } from "react";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";

import { AddVariantDialog } from "@/components/catalog/add-variant-dialog";
import { ItemDetailPage } from "@/components/catalog/item-detail-page";
import { ItemHistoryDrawer } from "@/components/catalog/item-history-drawer";
import { announceItemDeleted } from "@/hooks/items-error";
import { useCatalogItemDetail, useDeleteCatalogItem } from "@/hooks/catalog-items";
import { usePermissions } from "@/hooks/permissions";
import {
  displayedImages,
  displayedVariant,
  ownItemSelection,
  resolveVariantSelection,
  switchesToAddedVariant,
} from "@/lib/catalog-item-variants";
import { itemDetailIsLoading } from "@/lib/catalog-detail-loading";
import { ITEM_CATALOG_PATH, itemDetailUrl } from "@/lib/route-paths";
import { isStaleItemLink, requireCatalogAccess } from "@/lib/route-guards";

// One item's details page — the donor's `/items/:itemId`.
//
// A sibling of `index.tsx` rather than a child layout, as that file anticipated:
// the list and the details page share a URL prefix and nothing else.
//
// The route owns everything the page component deliberately does not — the two
// reads, the delete mutation, the clipboard, the snackbar and the navigation
// away from an item that no longer exists — which is what keeps every arm of
// the page testable without a router.

export const Route = createFileRoute("/_authed/_chrome/items/$itemId")({
  beforeLoad: ({ context }) => {
    requireCatalogAccess(context.currentUser);
  },
  head: () => ({ meta: [{ title: "Item Details" }] }),
  component: ItemDetailRoute,
});

/**
 * Whether the page is showing a **sibling** rather than the item the URL names.
 *
 * One comparison, and two separate behaviors turn on it — which is why it is one
 * named predicate rather than the same `!==` written twice:
 *
 *   - **Which read owns the division usage.** The donor keys its usage component
 *     on the variant, `<SpecUsage itemId={variantId} />`
 *     (`ItemDetailsIsland.tsx:182`), where every other child on that page takes
 *     the selected `item`. A sibling's usage is not in the group already in hand
 *     — the detail response carries the breakdown only for the item it was
 *     asked about — so showing a sibling means a second read.
 *   - **Where a successful delete leaves you.** The donor's
 *     `variantItemWasDeleted` is `itemIdDeleted !== activeItemId`, and it
 *     re-selects the active item and **stays on the page**
 *     (`ItemDetailsIsland/data/observers.ts:212-216`), where deleting the active
 *     item itself navigates away. That test sits *before* the `renderingMode`
 *     check, so it holds in page mode and dialog mode alike. Delete acts on the
 *     displayed variant, so the id it deleted *is* the displayed id — the same
 *     question this predicate already asks.
 *
 * The delete half is reachable here in a way it is not in modsy: the donor's own
 * page host wires no delete event at all (`pages/item-details-page.tsx:13-17`),
 * so the source's stated answer for the case is carried across even though no
 * modsy screen exercises it.
 *
 * Named and exported for the reason its neighbour above is: the two outcomes
 * are one comparison apart.
 */
export function isDisplayingSibling(urlItemId: number, displayedItemId: number): boolean {
  return displayedItemId !== urlItemId;
}

function ItemDetailRoute() {
  const { itemId } = Route.useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  // A path segment is a string, and the wire's id is an integer. `Number` on a
  // hand-edited URL yields `NaN`, which the server function's `z.int().positive()`
  // rejects at the seam — so `/items/abc` takes the read-failed arm and its
  // snackbar rather than reaching Python at all.
  const id = Number(itemId);

  const detail = useCatalogItemDetail(id);
  const remove = useDeleteCatalogItem();
  const [addingVariant, setAddingVariant] = useState(false);

  const data = detail.data ?? null;

  // Which variant of the group the page is showing. The URL never changes with
  // it — the donor keeps exactly this as local state beside its unchanged
  // `activeItemId` (`ItemDetailsIsland.tsx:86-98`).
  //
  // **And it is reset when the route moves to another item, not merely masked.**
  // The donor syncs on every change of the active item — "whenever the
  // activeItemId changes, we want to sync the variantId to it"
  // (`ItemDetailsIsland.tsx:88-93`) — and the distinction is observable because
  // this component is *reused* across `/items/:itemId` navigations rather than
  // remounted, so its `useState` survives them. Reading the `{ baseId,
  // variantId }` pair through `resolveVariantSelection` alone would only hide a
  // stale pick while the ids disagreed: go from item 7 with variant 9 selected
  // to item 10 and back to item 7, and the pair matches again and variant 9
  // returns, where modsy shows item 7.
  //
  // Reset during render rather than in an effect. An effect fires a commit late,
  // which is a frame of the previous item's variant on every navigation; this is
  // React's own answer for state that has to follow a changing input, and it
  // re-renders before paint. `resolveVariantSelection` still stands behind it —
  // it is what makes the pre-reset render show the URL's item rather than the
  // stale sibling — and it remains the list cards' whole mechanism, where an
  // instance really is reused for a different item and there is nothing to reset
  // against.
  const [selection, setSelection] = useState(() => ownItemSelection(id));
  if (selection.baseId !== id) {
    setSelection(ownItemSelection(id));
    // **And the Add Variant dialog goes with it.** It is mounted whether or not
    // it is open, so a route change under an open dialog leaves it up, holding
    // the previous item's typed draft against a *new* `parent` — submit then
    // creates a variant of the item the URL now names, out of values meant for
    // the one it no longer does. The Back button reaches this: the overlay
    // blocks clicks, not history. Closing it also empties the draft, because
    // the dialog puts its draft away on `open` going false however that
    // happened (`add-variant-dialog.tsx`, the `openSeen` transition).
    setAddingVariant(false);
  }
  const displayedId = resolveVariantSelection(selection, id);

  // The whole page follows the selection, not the URL: the donor selects its
  // `item` by the variant id (`ItemDetailsIsland.tsx:117`), and its action menu
  // then reads `item.id` for every entry — so Copy Link copies the *displayed*
  // variant's URL, Delete deletes it, and Add Variant adds a sibling to it.
  const shown = data ? displayedVariant(data.item, data.variants, displayedId) : null;
  const shownId = shown?.id ?? id;

  // **The division usage follows the displayed variant too**, and it is the one
  // thing on the page that cannot come out of the group already in hand: the
  // detail response carries the breakdown (`coreUsageCount`, `divisionUsage`)
  // for the item it was asked about and nothing else, while `variants` are item
  // summaries with no usage on them.
  //
  // The donor keys its usage component on the *variant* — `<SpecUsage
  // itemId={variantId} />` (`ItemDetailsIsland.tsx:182`), where every other
  // child on that page takes the selected `item` — so a sibling's total is
  // fetched when the sibling is shown. Reading `detail`'s usage under a sibling
  // would have printed that sibling's name and the base item's per-division
  // rows, which is worse than a loading state.
  //
  // **The gallery deliberately does not ride this read** (#413). It was the
  // tempting shape — this request already returns the sibling's `images` — but
  // it would have put a round trip between the click and the thumbnail strip,
  // where the donor has none, and left a *failed* read showing one picture of a
  // six-picture variant with nothing on the page saying so. The group's pictures
  // travel with the item instead; see `displayedImages`.
  //
  // Enabled only while a sibling is on screen, so the ordinary case fires no
  // second request; on a chip click this is the donor's own extra read.
  const showingSibling = isDisplayingSibling(id, shownId);
  const siblingDetail = useCatalogItemDetail(shownId, { enabled: showingSibling });
  const usage = showingSibling ? siblingDetail : detail;

  // A read that *succeeded* on an item that is not there is a stale link, and
  // the transport says what that renders: `getInternalCatalogItem` declares
  // `nullOn: CATALOG_ITEM_NOT_FOUND_TAG` precisely "so a stale link renders a
  // 404 page instead of throwing" (`server/data/internal-api/catalog.ts`). The
  // app's branded 404 is the router-wide default, so raising it here is the
  // whole implementation. A *failed* read is a different thing entirely and
  // keeps the donor's snackbar over the page below.
  if (isStaleItemLink(detail)) throw notFound();

  return (
    <>
      <ItemDetailPage
        item={shown}
        variants={data?.variants ?? []}
        onSelectVariant={(variantId) => setSelection({ baseId: id, variantId })}
        // Hero first, straight off the wire — the ordering is Python's
        // (`ItemDetailOutput.images`, ADR-0005 decision 9), so nothing here
        // re-sorts or re-derives a hero.
        //
        // **And they follow the chip, like everything else on the page** (#413).
        // The donor's `<Images item={item} />` draws the selected variant's own
        // set (`Lightbox/Images.tsx:26`) out of a cache that already holds every
        // sibling's pictures, so its swap is one commit with no request behind
        // it. The response carries the group's galleries for the same reason;
        // `displayedImages` is the lookup.
        images={displayedImages(data, shown)}
        coreUsageCount={usage.data?.coreUsageCount ?? 0}
        divisionUsage={usage.data?.divisionUsage ?? []}
        breakdownArrived={usage.data !== undefined}
        isLoading={itemDetailIsLoading(detail)}
        isError={detail.isError && !data}
        // The counts ride whichever read owns the displayed variant, so "the
        // usage is loading" is that read in flight — a refetch over data already
        // on screen for the URL's item, or the sibling's first fetch after a chip
        // click. "The usage failed" is that read failing while the page itself
        // stays, which is why it is still gated on the page having data.
        isUsageLoading={usage.isPending || usage.isFetching}
        isUsageError={usage.isError && Boolean(data)}
        // The donor's `itemPermissions.canEdit(permissionsItemData)`
        // (`ActionMenu.tsx:117`), over the *displayed* variant's usage: Python
        // evaluates the edit ladder on the same read as the breakdown (#666
        // D1), so Edit and Delete are offered exactly where a save would be
        // admitted. Until that read lands the menu shows its loading state, as
        // the donor's does, so no role sees an answer before there is one.
        //
        // **A read that failed** leaves no `mayEdit`, and the donor's
        // `canEdit` still answers the two roles its narrowing never applies to
        // before it looks at `divisions` (`usePermissions.ts:110-113`), while
        // a Division Editor gets nothing (`:87-89`). Those two roles are
        // `canBulkEditCatalogItems` — Python's bulk roles are its unrestricted
        // edit roles, the same object (`write_access.py`).
        canEdit={
          usage.data ? usage.data.mayEdit : usage.isError && permissions.canBulkEditCatalogItems
        }
        isDeleting={remove.isPending}
        deleteFailed={remove.isError}
        onDismissDeleteError={() => remove.reset()}
        onDelete={() => {
          remove.mutate(
            { itemId: shownId },
            {
              onSuccess: () => {
                // `ItemDeleteIsland`'s success snackbar. Fires for a sibling
                // and for the item itself alike; the donor's snackbar is not on
                // the branch below.
                announceItemDeleted(shownId);

                // A *sibling* was deleted: fall back to the item the URL names
                // and stay put, which is the donor's first branch — see
                // `isDisplayingSibling`. The group on screen loses the member
                // through the delete's own cache invalidation, so nothing else
                // has to be undone here.
                if (showingSibling) {
                  setSelection(ownItemSelection(id));
                  return;
                }

                // The item itself was deleted, so there is no page left to be
                // on. The donor's page mode returns to the last spec it had in
                // local storage, falling back to `/specifications/base`
                // (`observers.ts:217-229`) — neither exists in Atlas, and the
                // catalog this item was reached from is the standing substitute.
                void navigate({ to: ITEM_CATALOG_PATH });
              },
            },
          );
        }}
        onCopyLink={() => {
          void navigator.clipboard.writeText(itemDetailUrl(shownId));
        }}
        onAddVariant={() => setAddingVariant(true)}
        // The item History drawer (#1179) reads the item on screen, so a
        // variant chip switches what it reads, as it switches everything else
        // on the page. Drawn on this route only (ADR-0028 decision 14).
        history={shown ? <ItemHistoryDrawer itemId={shown.id} /> : undefined}
      />
      {/* The dialog is the route's, not the page's, for the same reason the
          delete is: it owns the write, and the page stays a component that draws
          what it is handed.

          Mounted always and told whether it is open, as the Request Item button
          mounts its own — a dialog unmounted on close has no close animation to
          play. It forgets its draft on the way out rather than on the way in,
          which is what makes that safe.

          `shown` rather than the URL's item — the donor's action menu reads
          `item.id` for every entry, so a variant is added as a sibling of
          whichever one is on screen. */}
      <AddVariantDialog
        open={addingVariant}
        onOpenChange={setAddingVariant}
        parent={shown}
        variants={data?.variants ?? []}
        // The donor's observer, guarded as the donor guards it — see
        // `switchesToAddedVariant`. The new sibling joins the group on screen
        // either way; that is the cache patch in
        // `addCatalogItemVariantMutationOptions`, not this. The URL stays put,
        // exactly as a chip click leaves it.
        onAdded={(variantId) => {
          if (!switchesToAddedVariant(id, shownId)) return;
          setSelection({ baseId: id, variantId });
        }}
      />
    </>
  );
}
